/**
 * FartSystem - Handles fart creation and damage calculations
 * 
 * Supports multiple fart types via FartInventory system.
 */
import Phaser from 'phaser';
import { Player } from '../entities/Player';
import { BasicFart } from '../entities/BasicFart';
import { LingeringFart } from '../entities/farts/LingeringFart';
import { GhostFart } from '../entities/farts/GhostFart';
import { ExplosiveFart } from '../entities/farts/ExplosiveFart';
import { SBDFart } from '../entities/farts/SBDFart';
import { FartType, FartInventory, FART_CONFIGS, PROPULSION_CONFIG } from '../entities/farts';
import { TileType } from './MazeGenerator';
import { networkManager } from '../network/NetworkManager';
import { StatsTracker } from './StatsTracker';
import {
    FartParams,
    calculateFartParams,
    calculateFartCost,
    calculateAffordablePercent,
    FART_MIN_COST
} from '../utils/FartCalculations';

// Re-export for convenience
export type { FartParams } from '../utils/FartCalculations';
export { calculateFartParams, calculateFartCost } from '../utils/FartCalculations';

// Broadcast callback type now includes fart type and optional direction for SBD
export type FartBroadcastCallback = (
    x: number,
    y: number,
    chargePercent: number,
    fartType: FartType,
    direction?: { x: number; y: number }
) => void;

export class FartSystem {
    private scene: Phaser.Scene;
    private player: Player;
    private farts: Phaser.Physics.Arcade.Group;
    private lingeringFarts: LingeringFart[] = [];  // Track for update loop
    private ghostFarts: GhostFart[] = [];  // Track for update loop and player targeting
    private explosiveFarts: ExplosiveFart[] = [];  // Track for damage detection
    private sbdFarts: SBDFart[] = [];  // Track for wall collision updates
    private mazeData?: TileType[][];  // For SBD wall detection
    private statsTracker: StatsTracker;
    private inventory: FartInventory;
    private onBroadcast?: FartBroadcastCallback;

    // Player references for ghost fart AI
    private allPlayers: Player[] = [];

    constructor(
        scene: Phaser.Scene,
        player: Player,
        farts: Phaser.Physics.Arcade.Group,
        statsTracker: StatsTracker
    ) {
        this.scene = scene;
        this.player = player;
        this.farts = farts;
        this.statsTracker = statsTracker;
        this.inventory = new FartInventory();
    }

    /**
     * Get the inventory for UI/selection
     */
    getInventory(): FartInventory {
        return this.inventory;
    }

    /**
     * Set callback for multiplayer broadcast
     */
    setOnBroadcast(callback: FartBroadcastCallback): void {
        this.onBroadcast = callback;
    }

    /**
     * Set all players for ghost fart targeting
     */
    setPlayers(players: Player[]): void {
        this.allPlayers = players;
    }

    /**
     * Set maze data for SBD wall collision detection
     */
    setMazeData(mazeData: TileType[][]): void {
        this.mazeData = mazeData;
    }

    /**
     * Update all active special farts
     */
    update(time: number, delta: number): void {
        // Update lingering farts and remove destroyed ones
        this.lingeringFarts = this.lingeringFarts.filter(fart => {
            if (fart.active) {
                fart.update(time, delta);
                return true;
            }
            return false;
        });

        // Update ghost farts
        this.ghostFarts = this.ghostFarts.filter(fart => {
            if (fart.active) {
                fart.update(time, delta);
                return true;
            }
            return false;
        });

        // Update SBD farts (need wall collision checks)
        this.sbdFarts = this.sbdFarts.filter(fart => {
            if (fart.active) {
                fart.update(time, delta);
                return true;
            }
            return false;
        });
    }

    /**
     * Release a fart based on currently selected type
     */
    releaseChargedFart(): void {
        const selectedType = this.inventory.getSelectedType();

        // Check if we can fire this type
        if (!this.inventory.canFire()) {
            // Fall back to basic
            this.releaseBasicFart();
            return;
        }

        switch (selectedType) {
            case FartType.BASIC:
                this.releaseBasicFart();
                break;
            case FartType.LINGERING:
                this.releaseLingeringFart();
                break;
            case FartType.GHOST:
                this.releaseGhostFart();
                break;
            case FartType.EXPLOSIVE:
                this.releaseExplosiveFart();
                break;
            case FartType.SBD:
                this.releaseSBDFart();
                break;
            case FartType.PROPULSION:
                this.releasePropulsionFart();
                break;
            default:
                // Unknown type, fall back to basic
                this.releaseBasicFart();
        }
    }

    /**
     * Release a basic fart (uses gas capacity, scales with charge)
     */
    private releaseBasicFart(): void {
        const chargePercent = this.player.stopCharging();

        // Calculate base cost
        let cost = calculateFartCost(chargePercent);
        let params = calculateFartParams(chargePercent);

        // Check capacity and adjust if needed
        if (this.player.capacity < cost) {
            const affordable = this.player.capacity;
            if (affordable < FART_MIN_COST) return;

            const affordablePercent = calculateAffordablePercent(affordable);
            this.player.useCapacity(affordable);

            const finalPercent = Math.min(chargePercent, affordablePercent);
            params = calculateFartParams(finalPercent);
        } else {
            this.player.useCapacity(cost);
        }

        // Create the fart
        this.createBasicFart(this.player.x, this.player.y, params, chargePercent);

        // Camera shake scales with charge
        this.scene.cameras.main.shake(50 + 100 * chargePercent, 0.002 + 0.008 * chargePercent);

        // Broadcast in multiplayer
        this.onBroadcast?.(this.player.x, this.player.y, chargePercent, FartType.BASIC);

        // Record stats
        this.statsTracker.recordFart(networkManager.playerId);
    }

    /**
     * Release a lingering fart (uses ammo, instant release - no charging)
     */
    private releaseLingeringFart(): void {
        // Stop charging animation (doesn't affect this fart, just cleanup)
        this.player.stopCharging();

        // Use ammo - if no ammo, just cancel (don't fall back to basic)
        if (!this.inventory.useAmmo()) {
            console.log('No ammo for Lingering Fart!');
            return;
        }

        // Create the lingering fart (no capacity cost!)
        this.createLingeringFart(this.player.x, this.player.y, networkManager.playerId);

        // Subtle camera shake
        this.scene.cameras.main.shake(80, 0.004);

        // Broadcast
        this.onBroadcast?.(this.player.x, this.player.y, 0, FartType.LINGERING);

        // Record stats
        this.statsTracker.recordFart(networkManager.playerId);
    }

    /**
     * Create a basic fart entity
     */
    createBasicFart(x: number, y: number, params: FartParams, chargePercent: number): BasicFart {
        const fart = new BasicFart(this.scene, x, y, {
            damage: params.damage,
            stunDuration: params.stunDuration,
            startScale: params.startScale,
            endScale: params.endScale,
        });

        fart.setData('ownerId', networkManager.playerId);
        fart.setData('chargePercent', chargePercent);
        fart.setDepth(40);
        this.farts.add(fart);

        return fart;
    }

    /**
     * Create a lingering fart entity
     */
    createLingeringFart(x: number, y: number, ownerId: string): LingeringFart {
        const fart = new LingeringFart(this.scene, x, y, {
            ownerId: ownerId,
        });

        this.farts.add(fart);
        this.lingeringFarts.push(fart);

        return fart;
    }

    /**
     * Release a ghost fart (uses ammo, instant release - no charging)
     */
    private releaseGhostFart(): void {
        // Stop charging animation (doesn't affect this fart, just cleanup)
        this.player.stopCharging();

        // Use ammo - if no ammo, just cancel (don't fall back to basic)
        if (!this.inventory.useAmmo()) {
            console.log('No ammo for Ghost Fart!');
            return;
        }

        // Create the ghost fart (no capacity cost!)
        this.createGhostFart(this.player.x, this.player.y, networkManager.playerId);

        // Eerie camera effect
        this.scene.cameras.main.shake(60, 0.003);

        // Broadcast
        this.onBroadcast?.(this.player.x, this.player.y, 0, FartType.GHOST);

        // Record stats
        this.statsTracker.recordFart(networkManager.playerId);
    }

    /**
     * Create a ghost fart entity
     */
    createGhostFart(x: number, y: number, ownerId: string): GhostFart {
        const fart = new GhostFart(this.scene, x, y, {
            ownerId: ownerId,
        });

        // Set all players as potential targets
        fart.setPlayers(this.allPlayers);

        this.farts.add(fart);
        this.ghostFarts.push(fart);

        return fart;
    }

    /**
     * Release an explosive fart (uses ammo, instant release - no charging)
     */
    private releaseExplosiveFart(): void {
        // Stop charging animation (doesn't affect this fart, just cleanup)
        this.player.stopCharging();

        // Use ammo - if no ammo, just cancel (don't fall back to basic)
        if (!this.inventory.useAmmo()) {
            console.log('No ammo for Explosive Fart!');
            return;
        }

        // Create the explosive fart (no capacity cost!)
        this.createExplosiveFart(this.player.x, this.player.y, networkManager.playerId);

        // Intense camera effect
        this.scene.cameras.main.shake(100, 0.005);

        // Broadcast
        this.onBroadcast?.(this.player.x, this.player.y, 0, FartType.EXPLOSIVE);

        // Record stats
        this.statsTracker.recordFart(networkManager.playerId);
    }

    /**
     * Create an explosive fart entity
     */
    createExplosiveFart(x: number, y: number, ownerId: string): ExplosiveFart {
        const fart = new ExplosiveFart(this.scene, x, y, {
            ownerId: ownerId,
        });

        this.farts.add(fart);
        this.explosiveFarts.push(fart);

        return fart;
    }

    /**
     * Release an SBD fart (uses ammo, instant release - fires in player direction)
     */
    private releaseSBDFart(): void {
        // Stop charging animation (doesn't affect this fart, just cleanup)
        this.player.stopCharging();

        // Use ammo - if no ammo, just cancel (don't fall back to basic)
        if (!this.inventory.useAmmo()) {
            console.log('No ammo for SBD Fart!');
            return;
        }

        // Need maze data for wall collision
        if (!this.mazeData) {
            console.warn('SBD Fart: No maze data set!');
            return;
        }

        // Get player facing direction from velocity or last movement
        const body = this.player.body as Phaser.Physics.Arcade.Body;
        let dirX = body.velocity.x;
        let dirY = body.velocity.y;

        // If not moving, default to facing right
        if (dirX === 0 && dirY === 0) {
            dirX = 1;
            dirY = 0;
        }

        // Normalize direction
        const len = Math.sqrt(dirX * dirX + dirY * dirY);
        const direction = { x: dirX / len, y: dirY / len };

        // Create the SBD fart (no capacity cost!)
        this.createSBDFart(this.player.x, this.player.y, networkManager.playerId, direction);

        // Subtle woosh camera effect
        this.scene.cameras.main.shake(30, 0.002);

        // Broadcast with direction for network sync
        this.onBroadcast?.(this.player.x, this.player.y, 0, FartType.SBD, direction);

        // Record stats
        this.statsTracker.recordFart(networkManager.playerId);
    }

    /**
     * Create an SBD fart entity
     */
    createSBDFart(x: number, y: number, ownerId: string, direction: { x: number; y: number }): SBDFart | null {
        if (!this.mazeData) return null;

        const fart = new SBDFart(this.scene, x, y, {
            ownerId: ownerId,
            direction: direction,
            mazeData: this.mazeData,
        });

        this.farts.add(fart);
        this.sbdFarts.push(fart);

        return fart;
    }

    /**
     * Release a propulsion fart (dash movement ability with i-frames)
     * NOT a projectile - it's a player movement buff!
     */
    private releasePropulsionFart(): void {
        // Stop charging animation
        this.player.stopCharging();

        // Use ammo - if no ammo, just cancel
        if (!this.inventory.useAmmo()) {
            console.log('No ammo for Propulsion Fart!');
            return;
        }

        // Get dash direction from player velocity
        const body = this.player.body as Phaser.Physics.Arcade.Body;
        let dirX = body.velocity.x;
        let dirY = body.velocity.y;

        // If not moving, use current facing (default right)
        if (dirX === 0 && dirY === 0) {
            dirX = 1;
            dirY = 0;
        }

        // Normalize
        const len = Math.sqrt(dirX * dirX + dirY * dirY);
        const normX = dirX / len;
        const normY = dirY / len;

        // Apply dash velocity
        body.setVelocity(
            normX * PROPULSION_CONFIG.dashSpeed,
            normY * PROPULSION_CONFIG.dashSpeed
        );

        // Create trail visual behind player
        this.createPropulsionTrail();

        // Apply invincibility visual
        this.player.setAlpha(0.5);
        this.player.setTint(0xffff00);

        // Flag for damage immunity
        (this.player as any)._propulsionActive = true;

        // End dash after duration
        this.scene.time.delayedCall(PROPULSION_CONFIG.dashDuration, () => {
            // Slow down but don't stop abruptly
            body.setVelocity(body.velocity.x * 0.5, body.velocity.y * 0.5);
        });

        // End invincibility after its duration
        this.scene.time.delayedCall(PROPULSION_CONFIG.invincibilityDuration, () => {
            this.player.setAlpha(1);
            this.player.setTint(this.player.color);
            (this.player as any)._propulsionActive = false;
        });

        // Camera effect - motion blur feel
        this.scene.cameras.main.shake(50, 0.003);

        // Broadcast
        this.onBroadcast?.(this.player.x, this.player.y, 0, FartType.PROPULSION);

        // Record stats
        this.statsTracker.recordFart(networkManager.playerId);
    }

    /**
     * Create propulsion trail effect behind player
     */
    private createPropulsionTrail(): void {
        const trailCount = 5;

        for (let i = 0; i < trailCount; i++) {
            this.scene.time.delayedCall(i * 50, () => {
                const trail = this.scene.add.graphics();
                trail.setDepth(35);
                trail.fillStyle(0x8b4513, 0.6);
                trail.fillCircle(this.player.x, this.player.y, 10 - i);

                // Fade out
                this.scene.tweens.add({
                    targets: trail,
                    alpha: 0,
                    scaleX: 2,
                    scaleY: 2,
                    duration: 300,
                    onComplete: () => trail.destroy()
                });
            });
        }
    }

    /**
     * Check if player is currently in propulsion dash (for damage immunity)
     */
    isPlayerDashing(): boolean {
        return (this.player as any)._propulsionActive === true;
    }

    /**
     * Create a remote fart (for multiplayer sync)
     */
    createRemoteFart(x: number, y: number, chargePercent: number, fartType: FartType, ownerId: string): void {
        switch (fartType) {
            case FartType.BASIC:
                const params = calculateFartParams(chargePercent);
                const fart = new BasicFart(this.scene, x, y, {
                    damage: params.damage,
                    stunDuration: params.stunDuration,
                    startScale: params.startScale,
                    endScale: params.endScale,
                });
                fart.setData('ownerId', ownerId);
                fart.setData('chargePercent', chargePercent);
                fart.setDepth(40);
                this.farts.add(fart);
                break;

            case FartType.LINGERING:
                this.createLingeringFart(x, y, ownerId);
                break;

            case FartType.GHOST:
                this.createGhostFart(x, y, ownerId);
                break;

            case FartType.EXPLOSIVE:
                this.createExplosiveFart(x, y, ownerId);
                break;

            // Future types handled here
            default:
                // Fallback to basic for unknown types
                const defaultParams = calculateFartParams(chargePercent);
                const defaultFart = new BasicFart(this.scene, x, y, {
                    damage: defaultParams.damage,
                    stunDuration: defaultParams.stunDuration,
                    startScale: defaultParams.startScale,
                    endScale: defaultParams.endScale,
                });
                defaultFart.setData('ownerId', ownerId);
                this.farts.add(defaultFart);
        }
    }

    /**
     * Get active lingering farts (for collision detection)
     */
    getLingeringFarts(): LingeringFart[] {
        return this.lingeringFarts.filter(f => f.active);
    }

    /**
     * Get active ghost farts (for collision detection)
     */
    getGhostFarts(): GhostFart[] {
        return this.ghostFarts.filter(f => f.active);
    }

    /**
     * Get active explosive farts (for collision detection)
     */
    getExplosiveFarts(): ExplosiveFart[] {
        return this.explosiveFarts.filter(f => f.active);
    }

    /**
     * Get active SBD farts (for collision detection)
     */
    getSBDFarts(): SBDFart[] {
        return this.sbdFarts.filter(f => f.active);
    }

    /**
     * Clean up resources
     */
    destroy(): void {
        this.lingeringFarts.forEach(fart => {
            if (fart.active) fart.destroy();
        });
        this.lingeringFarts = [];

        this.ghostFarts.forEach(fart => {
            if (fart.active) fart.destroy();
        });
        this.ghostFarts = [];

        this.explosiveFarts.forEach(fart => {
            if (fart.active) fart.destroy();
        });
        this.explosiveFarts = [];

        this.sbdFarts.forEach(fart => {
            if (fart.active) fart.destroy();
        });
        this.sbdFarts = [];

        this.inventory.reset();
    }
}
